/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef, useState } from 'react';

import { cssMap, jsx } from '@atlaskit/css';

type Point = [number, number];

const NUM_POINTS = 24;
const CX = 16;
const CY = 16;
const R = 9;

// All 24 angles: starting at -90° (12 o'clock), going clockwise at 15° intervals
const ANGLES = Array.from({ length: NUM_POINTS }, (_, i) => (-90 + i * (360 / NUM_POINTS)) * (Math.PI / 180));

/**
 * Cast a ray from center (cx, cy) at angle θ and find where it intersects
 * the polygon defined by the given vertices. Returns the intersection point.
 */
const rayIntersectsPolygon = (angle: number, vertices: Point[]): Point => {
	const dx = Math.cos(angle);
	const dy = Math.sin(angle);
	let bestT = Infinity;
	let result: Point = [CX + dx * R, CY + dy * R];

	for (let i = 0; i < vertices.length; i++) {
		const [x1, y1] = vertices[i];
		const [x2, y2] = vertices[(i + 1) % vertices.length];

		// Parametric ray: P = (CX, CY) + t*(dx, dy)
		// Parametric segment: Q = (x1, y1) + s*((x2-x1), (y2-y1))
		// Solve for t and s
		const edgeDx = x2 - x1;
		const edgeDy = y2 - y1;
		const denom = dx * edgeDy - dy * edgeDx;

		if (Math.abs(denom) < 1e-10) continue; // parallel

		const t = ((x1 - CX) * edgeDy - (y1 - CY) * edgeDx) / denom;
		const s = ((x1 - CX) * dy - (y1 - CY) * dx) / denom;

		if (t >= 0 && s >= 0 && s <= 1 && t < bestT) {
			bestT = t;
			result = [CX + t * dx, CY + t * dy];
		}
	}

	return result;
};

// Circle: 24 points at exact angles on circumference
const circlePoints: Point[] = ANGLES.map((angle) => [CX + R * Math.cos(angle), CY + R * Math.sin(angle)]);

// Square: matching reference exactly — half-side=8 (inscribed radius=8)
// Vertices: top-left(8,8), top-right(24,8), bottom-right(24,24), bottom-left(8,24)
const squareVertices: Point[] = [
	[8, 8],   // top-left
	[24, 8],  // top-right
	[24, 24], // bottom-right
	[8, 24],  // bottom-left
];
const squarePoints: Point[] = ANGLES.map((angle) => rayIntersectsPolygon(angle, squareVertices));

// Triangle: matching reference exactly
// Vertices: apex(16,8), bottom-right(24.66,23.75), bottom-left(7.34,23.75)
const triangleVertices: Point[] = [
	[16, 8],
	[24.66, 23.75],
	[7.34, 23.75],
];
const trianglePoints: Point[] = ANGLES.map((angle) => rayIntersectsPolygon(angle, triangleVertices));

// Hexagon: 6 vertices at 60° intervals, then sample at same 24 angles
const hexagonVertices: Point[] = Array.from({ length: 6 }, (_, i) => {
	const angle = (i * 60 - 90) * (Math.PI / 180);
	return [CX + R * Math.cos(angle), CY + R * Math.sin(angle)];
}) as Point[];
const hexagonPoints: Point[] = ANGLES.map((angle) => rayIntersectsPolygon(angle, hexagonVertices));

// Cycle: Circle → Square → Triangle → Hexagon → (back to Circle)
const SHAPE_POINTS: Point[][] = [circlePoints, squarePoints, trianglePoints, hexagonPoints];

const SHAPE_COLORS = [
	'#FCA700', // Orange (Circle)
	'#6A9A23', // Green (Square)
	'#1868DB', // Blue (Triangle)
	'#AF59E1', // Purple (Hexagon)
];

// Transition duration in ms — matching reference (0.6s)
const TRANSITION_DURATION = 600;

// Module-level state — persists across remounts so animation doesn't restart
// when new trace steps cause MorphingLoader to remount.
let globalFromIndex = 0;
let globalToIndex = 1;
let globalProgress = 0; // 0→1 raw progress between fromIndex and toIndex

// Easing: backOut — subtle overshoot for a springy feel (matching reference)
const easeBackOut = (t: number): number => {
	const c1 = 1.70158;
	const c3 = c1 + 1;
	return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

// Parse hex color to [r, g, b]
const hexToRgb = (hex: string): [number, number, number] => {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return [r, g, b];
};

// Lerp two numbers
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

// Interpolate between two sets of points
const interpolatePoints = (from: Point[], to: Point[], t: number): Point[] =>
	from.map(([fx, fy], i) => [lerp(fx, to[i][0], t), lerp(fy, to[i][1], t)]);

// Interpolate between two hex colors
const interpolateColor = (fromHex: string, toHex: string, t: number): string => {
	const [fr, fg, fb] = hexToRgb(fromHex);
	const [tr, tg, tb] = hexToRgb(toHex);
	const r = Math.round(lerp(fr, tr, t));
	const g = Math.round(lerp(fg, tg, t));
	const b = Math.round(lerp(fb, tb, t));
	return `rgb(${r},${g},${b})`;
};

// Build SVG path string from N points
const buildPath = (points: Point[]): string => {
	const [first, ...rest] = points;
	return `M ${first[0].toFixed(2)} ${first[1].toFixed(2)} ${rest.map(([x, y]) => `L ${x.toFixed(2)} ${y.toFixed(2)}`).join(' ')} Z`;
};

const styles = cssMap({
	container: {
		width: '24px',
		height: '24px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'visible',
	},
	svg: {
		overflow: 'visible',
	},
});

type MorphingLoaderProps = {
	testId?: string;
};

export const MorphingLoader = ({ testId }: MorphingLoaderProps): JSX.Element => {
	// Initialise from global state so remounts continue from where animation left off
	const [pathData, setPathData] = useState<string>(() =>
		buildPath(
			interpolatePoints(
				SHAPE_POINTS[globalFromIndex],
				SHAPE_POINTS[globalToIndex],
				easeBackOut(Math.min(globalProgress, 1)),
			),
		),
	);
	const [fillColor, setFillColor] = useState<string>(() =>
		interpolateColor(
			SHAPE_COLORS[globalFromIndex],
			SHAPE_COLORS[globalToIndex],
			Math.min(globalProgress, 1),
		),
	);

	const rafRef = useRef<number | null>(null);
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;

		const animate = (startTime: number) => {
			const elapsed = performance.now() - startTime;
			const rawProgress = Math.min(elapsed / TRANSITION_DURATION, 1);
			const easedProgress = easeBackOut(rawProgress);

			// Sync global progress for remount continuity
			globalProgress = rawProgress;

			const fromPoints = SHAPE_POINTS[globalFromIndex];
			const toPoints = SHAPE_POINTS[globalToIndex];
			const fromColor = SHAPE_COLORS[globalFromIndex];
			const toColor = SHAPE_COLORS[globalToIndex];

			setPathData(buildPath(interpolatePoints(fromPoints, toPoints, easedProgress)));
			setFillColor(interpolateColor(fromColor, toColor, rawProgress));

			if (rawProgress < 1) {
				rafRef.current = requestAnimationFrame(() => animate(startTime));
			} else {
				// Transition complete — advance to next shape immediately (continuous, no pause)
				globalFromIndex = globalToIndex;
				globalToIndex = (globalToIndex + 1) % SHAPE_POINTS.length;
				globalProgress = 0;

				if (mountedRef.current) {
					rafRef.current = requestAnimationFrame(() => animate(performance.now()));
				}
			}
		};

		// Start from current global progress position (handles remount mid-transition)
		const startTime = performance.now() - globalProgress * TRANSITION_DURATION;
		rafRef.current = requestAnimationFrame(() => animate(startTime));

		return () => {
			mountedRef.current = false;
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
		};
	}, []);

	return (
		<div css={styles.container} data-testid={testId}>
			<svg width="28" height="28" viewBox="0 0 32 32" css={styles.svg}>
				<path
					d={pathData}
					fill={fillColor}
					stroke="none"
					strokeLinejoin="round"
					strokeLinecap="round"
				/>
			</svg>
		</div>
	);
};
